import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  cancelTrainingRun,
  createLora,
  deleteLora,
  setDefaultCheckpoint,
  submitTrainingRun,
  transferLoraToTeam,
  transferLoraToUser,
  updateLora,
} from "../../services/lorasApi";
import { loraQueryKeys, trainingRunQueryKeys } from "./queryKeys";
import type { Lora, UpdateLoraBody } from "../../types/loras";

export function useCreateLora() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLora,
    onSuccess: (lora: Lora) => {
      queryClient.invalidateQueries({ queryKey: loraQueryKeys.lists() });
      queryClient.setQueryData(loraQueryKeys.detail(lora.id), lora);
    },
  });
}

export function useUpdateLora(loraId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateLoraBody) => updateLora(loraId, body),
    onSuccess: (lora: Lora) => {
      queryClient.setQueryData(loraQueryKeys.detail(lora.id), lora);
      queryClient.invalidateQueries({ queryKey: loraQueryKeys.lists() });
    },
  });
}

export function useDeleteLora() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLora,
    onSuccess: (_data, loraId) => {
      queryClient.invalidateQueries({ queryKey: loraQueryKeys.lists() });
      queryClient.removeQueries({ queryKey: loraQueryKeys.detail(loraId) });
    },
  });
}

export function useTransferLoraToTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loraId, teamId }: { loraId: string; teamId: string }) =>
      transferLoraToTeam(loraId, teamId),
    onSuccess: (lora) => {
      queryClient.setQueryData(loraQueryKeys.detail(lora.id), lora);
      queryClient.invalidateQueries({ queryKey: loraQueryKeys.lists() });
    },
  });
}

export function useTransferLoraToUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: transferLoraToUser,
    onSuccess: (lora) => {
      queryClient.setQueryData(loraQueryKeys.detail(lora.id), lora);
      queryClient.invalidateQueries({ queryKey: loraQueryKeys.lists() });
    },
  });
}

export function useSubmitTraining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      loraId,
      params,
      mock,
    }: {
      loraId: string;
      params: Record<string, unknown>;
      /** When true, uses mock training (30s per checkpoint) instead of real AWS Batch. */
      mock?: boolean;
    }) => submitTrainingRun(loraId, params, mock),
    onSuccess: (run) => {
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.detail(run.loraId),
      });
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.trainingRuns(run.loraId),
      });
      queryClient.setQueryData(trainingRunQueryKeys.detail(run.id), {
        ...run,
        checkpoints: [],
      });
    },
  });
}

export function useCancelTraining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelTrainingRun,
    onSuccess: (run) => {
      queryClient.invalidateQueries({
        queryKey: trainingRunQueryKeys.detail(run.id),
      });
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.detail(run.loraId),
      });
    },
  });
}

export function useSetDefaultCheckpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      loraId,
      checkpointId,
    }: {
      loraId: string;
      checkpointId: string;
    }) => setDefaultCheckpoint(loraId, checkpointId),
    onSuccess: (result) => {
      // The API returns only { id, defaultCheckpointId } — merge that into the
      // cached LoRA rather than replacing it, otherwise name/triggerWord/etc.
      // get wiped (which crashed the wizard's LockSlide derivation).
      queryClient.setQueryData<Lora | undefined>(
        loraQueryKeys.detail(result.id),
        (old) =>
          old
            ? { ...old, defaultCheckpointId: result.defaultCheckpointId }
            : old,
      );
      // Refetch the authoritative full record + checkpoint list.
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.detail(result.id),
      });
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.checkpoints(result.id),
      });
    },
  });
}
